import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, FileText, Mail, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <div className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-[#C9A227] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Politique de Confidentialité</h1>
          <p className="text-gray-400">Dernière mise à jour : 4 mars 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-[#C9A227]" />
              <h2 className="font-serif text-2xl text-[#1A1A1A] m-0">Introduction</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Excelsium Conseils ("nous", "notre", "nos") s'engage à protéger la confidentialité de vos données personnelles. 
              Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations 
              lorsque vous utilisez notre site web www.excelsium.online et nos services.
            </p>
          </section>

          {/* Data Collection */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-[#C9A227]" />
              <h2 className="font-serif text-2xl text-[#1A1A1A] m-0">Données Collectées</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4">Nous collectons les informations suivantes :</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Informations de contact</strong> : nom, prénom, adresse email, numéro de téléphone</li>
              <li><strong>Informations de demande</strong> : type de service demandé, message</li>
              <li><strong>Données techniques</strong> : adresse IP, type de navigateur, pages visitées (anonymisées)</li>
            </ul>
          </section>

          {/* Use of Data */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-[#C9A227]" />
              <h2 className="font-serif text-2xl text-[#1A1A1A] m-0">Utilisation des Données</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4">Vos données sont utilisées pour :</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Répondre à vos demandes de renseignements et devis</li>
              <li>Fournir nos services de conseil et formation</li>
              <li>Améliorer notre site web et nos services</li>
              <li>Vous contacter concernant nos services (avec votre consentement)</li>
            </ul>
          </section>

          {/* Data Protection */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-[#C9A227]" />
              <h2 className="font-serif text-2xl text-[#1A1A1A] m-0">Protection des Données</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données personnelles contre tout accès, 
              modification, divulgation ou destruction non autorisés. Ces mesures comprennent le chiffrement SSL/TLS, 
              des serveurs sécurisés et des contrôles d'accès stricts.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#1A1A1A] mb-4">Partage des Données</h2>
            <p className="text-gray-600 leading-relaxed">
              Nous ne vendons, n'échangeons ni ne louons vos données personnelles à des tiers. Vos informations peuvent être 
              partagées uniquement avec des prestataires de services essentiels (hébergement, email) dans le cadre strict 
              de la fourniture de nos services, et ces prestataires sont tenus de respecter la confidentialité de vos données.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#1A1A1A] mb-4">Vos Droits</h2>
            <p className="text-gray-600 leading-relaxed mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Droit d'accès</strong> : obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification</strong> : corriger vos données inexactes</li>
              <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#1A1A1A] mb-4">Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              Notre site utilise des cookies essentiels pour assurer son bon fonctionnement. Ces cookies ne collectent 
              pas d'informations personnelles identifiables et sont nécessaires pour la navigation sur le site.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12 bg-[#1A1A1A] text-white p-8 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-[#C9A227]" />
              <h2 className="font-serif text-2xl m-0">Nous Contacter</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-4">
              Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits :
            </p>
            <div className="space-y-2 text-gray-300">
              <p><strong className="text-white">Email :</strong> direction@excelsium.online</p>
              <p><strong className="text-white">Téléphone :</strong> +216 96 050 699</p>
              <p><strong className="text-white">Adresse :</strong> Pacha Centre, Montplaisir, Tunis, Tunisie</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
